import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  approvePost, getPost, postMedia, rejectPost, retryDelivery, signMedia, thumbPath, thumbTargets,
} from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { logError } from "../lib/logger";
import { formatBytes } from "../lib/media";
import { Thumb } from "../components/Thumb";
import { Spinner } from "../components/Spinner";
import { DeliveryRow } from "../components/DeliveryRow";
import { DeletePostDialog } from "../components/DeletePostDialog";
import { DeleteDeliveryDialog } from "../components/DeleteDeliveryDialog";
import { useWorkspace } from "../lib/WorkspaceContext";
import type { Delivery, Post } from "../lib/types";

export function PostDetail() {
  // Retrying re-sends content to a live audience, so it carries the same
  // authority publishing does (ADR-0011). Deleting carries its own, `post.delete`,
  // which admins and owners hold and a creator does not.
  const { can } = useWorkspace();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Signed thumbnails for the post's photos, by storage path. Absent is normal:
  // a file deleted from the library since publishing has no bytes left to show.
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  // Signed originals for videos somebody actually pressed play on, by asset id.
  //
  // This page used to sign the original for EVERY video on load, alongside the
  // poster. `preload="none"` meant the bytes usually never moved — but a mint is
  // metered at the object's full size whether or not it is fetched, so a page
  // with three clips reported tens of megabytes for showing three stills. It was
  // the worst bytes-per-mint of any surface in the product (8.4 MB, against the
  // calendar's 0.18) and almost all of it was imaginary.
  const [playable, setPlayable] = useState<Map<string, string>>(new Map());
  /** The video whose URL is being minted. The ring goes on the tile clicked. */
  const [starting, setStarting] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  // The destination a removal is being confirmed over, or null. Separate from
  // the post-wide confirmation above because they are different losses.
  const [removing, setRemoving] = useState<Delivery | null>(null);
  // Which review button is waiting on the server, if any.
  const [reviewing, setReviewing] = useState<"approve" | "reject" | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const row = await getPost(id);
      setPost(row);
      const assets = postMedia(row).map((m) => m.media_assets!);
      // A post with nothing left to show is not an error, so a failed signing
      // must not become one — the deliveries below are the reason this page
      // exists and they render regardless.
      if (assets.length > 0) {
        // Derivatives and NOTHING ELSE (0017-v2 §2). A ten-photo carousel used
        // to be ten full-size downloads on a page that only shows them side by
        // side; a video's original is now minted when somebody presses play.
        setUrls(await signMedia(thumbTargets(assets), { surface: "post" }).catch(() => new Map()));
      }
    } catch (e: any) {
      setError(e.message);
      void logError("post_view", e, { step: "load_post", postId: id });
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Mint the original, once, for the clip somebody wants to watch.
   *
   * `signMedia` keeps a live URL per path for the length of this tab's visit, so
   * pausing and replaying costs nothing further — and the 5-second poll below
   * cannot re-mint it either, because the poll never asks for originals at all.
   */
  const play = useCallback(async (assetId: string, target: Parameters<typeof signMedia>[0][number]) => {
    setStarting(assetId);
    try {
      const signed = await signMedia([target], { surface: "post" });
      const url = signed.get(typeof target === "string" ? target : target.storage_path);
      if (url) setPlayable((prev) => new Map(prev).set(assetId, url));
    } catch (e: any) {
      void logError("post_view", e, { step: "sign_video", postId: id });
    } finally {
      setStarting(null);
    }
  }, [id]);

  // Deliveries move on the scheduler's clock. Not while a confirmation is open,
  // though: a refresh landing under the dialog would move the page somebody is
  // in the middle of reading, and a 404 mid-delete would replace it with an
  // error for a post they meant to delete anyway. Not while the tab is hidden
  // either (0017-v2 §0A).
  //
  // This poll used to re-sign the post's media on every tick, five seconds
  // apart. `signMedia` now returns the URL it already holds, so the `src` on
  // the <video> below is the same string it was — which is what stops a
  // refresh from tearing down and restarting a video somebody is watching.
  usePoll(load, 5_000, { paused: confirming || removing !== null });

  async function retry(deliveryId: string) {
    try {
      await retryDelivery(deliveryId);
      await load();
    } catch (e: any) {
      setError(e.message);
      void logError("delivery_retry", e, { step: "retry_delivery", postId: id ?? null, deliveryId });
    }
  }

  async function review(action: "approve" | "reject") {
    if (!id || reviewing) return;
    setReviewing(action);
    setError(null);
    try {
      if (action === "approve") await approvePost(id);
      else await rejectPost(id, null);
      await load();
    } catch (e: any) {
      setError(e.message);
      void logError("post_view", e, { step: `review_${action}`, postId: id });
    } finally {
      setReviewing(null);
    }
  }

  if (error && !post) return <div className="card err">{error}</div>;
  if (!post) return <p className="muted">Loading…</p>;

  const deliveries = post.deliveries ?? [];
  const published = deliveries.filter((d) => d.status === "published").length;
  const failed = deliveries.filter((d) => d.status === "failed").length;
  const media = postMedia(post);
  // Every slide, or none: `post_media` holds one row even for a single-photo
  // post, so an empty list means the files have been deleted from the library
  // since — which is allowed, and leaves the record intact (20260828000900).
  const missing = (post.post_media?.length ?? 0) - media.length;

  return (
    <>
      <p><Link to="/posts">← All posts</Link></p>
      <h1>{post.title || post.caption.slice(0, 60) || "Post"}</h1>

      {post.approval === "pending" && (
        <div className="card">
          <div className="notice">
            Waiting for approval. Nothing is published until it is approved.
            {post.scheduled_at && ` Scheduled for ${new Date(post.scheduled_at).toLocaleString()}.`}
          </div>
          <p>
            <button className="primary" disabled={!!reviewing} onClick={() => review("approve")}>
              {reviewing === "approve" ? <><Spinner /> Approving…</> : "Approve"}
            </button>{" "}
            <button disabled={!!reviewing} onClick={() => review("reject")}>
              {reviewing === "reject" ? <><Spinner /> Sending back…</> : "Send back to draft"}
            </button>
          </p>
        </div>
      )}
      {post.approval === "rejected" && post.status === "draft" && (
        <div className="card">
          <div className="notice">
            Sent back to draft{post.review_note ? `: ${post.review_note}` : "."} Create it again from Compose to publish.
          </div>
        </div>
      )}

      {/* Partial failure is the normal case (ADR-0007) — say so plainly rather
          than showing one aggregate tick. */}
      {published > 0 && failed > 0 && (
        <div className="card">
          <div className="notice">
            Published to {published} of {deliveries.length} networks. The {failed} below failed and
            can be retried independently — the networks that succeeded are untouched.
          </div>
        </div>
      )}

      {(media.length > 0 || missing > 0) && (
        <div className="card">
          <h2>{media.length > 1 ? `${media.length} photos` : "Media"}</h2>
          {media.length > 1 && (
            <p className="muted">
              Published as one post — a carousel on Instagram, a multi-photo post on Facebook.
            </p>
          )}
          <ol className="post-gallery">
            {media.map((m, i) => {
              const asset = m.media_assets!;
              const video = asset.mime_type.startsWith("video/");
              const key = thumbPath(asset);
              const poster = key === null ? undefined : urls.get(key);
              // Only after somebody asked for it. Until then this slide is a
              // still, and the original has not been minted or metered.
              const watching = playable.get(asset.id);
              return (
                <li key={asset.id + i} className="post-slide">
                  <span className="thumb">
                    {video && watching
                      ? <video src={watching} controls autoPlay poster={poster} />
                      : video
                        ? (
                          <button
                            type="button"
                            className="play-over"
                            aria-label={`Play ${asset.original_filename ?? "video"}`}
                            disabled={starting === asset.id}
                            onClick={() => void play(asset.id, {
                              storage_path: asset.storage_path,
                              byte_size: asset.byte_size,
                              workspace_id: asset.workspace_id,
                            })}
                          >
                            <Thumb asset={asset} url={poster} alt="" />
                            <span className="play-badge" aria-hidden="true">
                              {starting === asset.id ? "…" : "▶"}
                            </span>
                          </button>
                        )
                        : <Thumb asset={asset} url={poster} alt={`Photo ${i + 1}`} />}
                    {media.length > 1 && <span className="pick-badge">{i + 1}</span>}
                  </span>
                  <span className="muted media-meta">
                    {asset.original_filename ?? "Untitled"} · {formatBytes(asset.byte_size)}
                  </span>
                </li>
              );
            })}
          </ol>
          {missing > 0 && (
            <p className="muted">
              {missing === 1 ? "One file has" : `${missing} files have`} been deleted from your
              library since this post went out. What was published is unaffected.
            </p>
          )}
        </div>
      )}

      <div className="card">
        <h2>Networks</h2>
        {deliveries.map((d) => (
          <DeliveryRow
            key={d.id}
            d={d}
            onRetry={can("delivery.retry") ? retry : undefined}
            // Not offered on the last one: the database refuses it (a post with
            // no destinations recomputes to `draft` having been published), and
            // a button whose only outcome is that refusal is worse than no
            // button. "Delete this post" below is what that person wants.
            onDelete={can("post.delete") && deliveries.length > 1 ? setRemoving : undefined}
          />
        ))}
      </div>

      {post.caption && (
        <div className="card">
          <h2>Caption</h2>
          <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{post.caption}</p>
        </div>
      )}

      {error && <div className="card err">{error}</div>}

      {can("post.delete") && (
        <div className="card">
          <h2>Delete this post</h2>
          <p className="muted">
            {published > 0
              ? "Removes our record of this post. It stays published on the networks it already reached."
              : "Cancels this post before it goes out. Your photos stay in the media library."}
          </p>
          <button className="danger" onClick={() => setConfirming(true)}>Delete this post</button>
        </div>
      )}

      {/* The same dialog the posts list opens. The sentence that has to be read
          before a post goes is written once, in one component, so the two
          screens cannot come to disagree about what deleting actually does. */}
      {removing && (
        <DeleteDeliveryDialog
          delivery={removing}
          postId={post.id}
          onCancel={() => setRemoving(null)}
          onDeleted={() => {
            setRemoving(null);
            // Reload rather than splice: removing a destination rewrites the
            // post's status from what is left (a post that loses its one failed
            // network becomes `published`), and that recompute happens in the
            // database, so the screen has to ask rather than guess.
            void load();
          }}
        />
      )}

      {confirming && (
        <DeletePostDialog
          postId={post.id}
          label={post.title || post.caption.slice(0, 80) || "(no caption)"}
          published={published}
          onCancel={() => setConfirming(false)}
          onDeleted={() => navigate("/posts")}
        />
      )}
    </>
  );
}
